import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { getCSRFToken } from '../utils/csrf';
import DroppableGroupCTE from './DroppableGroupCTE';
import UnassignedStudentsCTE from './UnassignedStudentsCTE';
import RemoveGroup from './RemoveGroup';

import './Groups.css';

const ManageCTEGroups = () => {
    const [groupData, setGroupData] = useState({
        cte: { groups: [], unassignedStudents: [] }, // Simplified to just CTE groups
    });
    const [newGroupName, setNewGroupName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [selectedGradeLevel, setSelectedGradeLevel] = useState('');
    // Keep a stable snapshot of the last valid (truthy) grade level to avoid race conditions during DnD
    const lastValidGradeLevelRef = useRef('');
    const [gradeLevels, setGradeLevels] = useState([]);

    // Fetch grade levels
    useEffect(() => {
        axios.get('/courses/api/grade_levels/')
            .then(response => setGradeLevels(response.data))
            .catch(error => console.error('Error fetching grade levels:', error));
    }, []);

    // Fetch CTE groups/unassigned by grade level
    const fetchGradeLevelData = async (gradeLevel) => {
        console.log(`CTE: fetching data for gradeLevel=${gradeLevel}`);
        if (!gradeLevel) {
            console.warn('CTE: skip fetch, no grade level selected');
            return;
        }
        setIsLoading(true);
        try {
            const endpoint = `/api/groups/cte-groups/grade-level-data/${gradeLevel}/`;
            const response = await axios.get(endpoint);
            const data = response.data || {};
            console.log('CTE: fetched data:', data);
            setGroupData({
                cte: {
                    groups: Array.isArray(data.groups) ? data.groups : [],
                    unassignedStudents: Array.isArray(data.unassigned_students)
                        ? data.unassigned_students
                        : [],
                },
            });
        } catch (error) {
            console.error('CTE: error fetching data', error);
            alert('Error fetching CTE data.');
        } finally {
            setIsLoading(false);
        }
    };

    // Trigger fetch on grade level change
    useEffect(() => {
        // Update last valid grade snapshot if current selection is truthy
        if (selectedGradeLevel) {
            lastValidGradeLevelRef.current = selectedGradeLevel;
        }
        fetchGradeLevelData(selectedGradeLevel);
    }, [selectedGradeLevel]);

    // Handle drop action
    const handleDrop = async (item, targetGroup) => {
        // Snapshot grade level to avoid race if user switches tab mid-op.
        // If temporarily falsy (e.g., React state update timing), fall back to last known valid value.
        const opGradeLevel = selectedGradeLevel || lastValidGradeLevelRef.current;
        console.log('CTE drag item:', item);
        console.log('CTE target group:', targetGroup);

        if (!opGradeLevel) {
            alert('Please select a grade level before moving students.');
            return;
        }

        try {
            const endpoint = '/api/groups/cte-groups/update-membership/';

            if (!targetGroup) {
                // Move to unassigned
                await axios.post(
                    endpoint,
                    {
                        student_id: item.id,
                        group_id: null,
                        grade_level: Number(opGradeLevel),
                    },
                    { headers: { 'X-CSRFToken': getCSRFToken() }, withCredentials: true }
                );
                // Refetch canonical state to avoid transient disappearance
                await fetchGradeLevelData(opGradeLevel);
                console.log('CTE: moved to unassigned (refetched).');
                return;
            }

            // Move between groups
            await axios.post(
                endpoint,
                {
                    student_id: item.id,
                    group_id: targetGroup.id,
                    grade_level: Number(opGradeLevel),
                },
                { headers: { 'X-CSRFToken': getCSRFToken() }, withCredentials: true }
            );

            // Refetch canonical state instead of optimistic local changes
            await fetchGradeLevelData(opGradeLevel);
            console.log('CTE: moved between groups (refetched).');
        } catch (error) {
            console.error('CTE: handleDrop error:', error?.message || error);
            alert('Error updating CTE membership: ' + (error?.message || error));
        }
    };

    // Removed auto-create teacher-specific groups; CTE groups are teacher-agnostic

    const addGroupManually = async () => {
        if (!newGroupName.trim()) {
            alert('Please enter a group name.');
            return;
        }

        if (!selectedGradeLevel) {
            alert('Please select a grade level first.');
            return;
        }

        try {
            const response = await axios.post(
                '/api/groups/cte-groups/add/',
                {
                    name: newGroupName,
                    grade_level: Number(selectedGradeLevel),
                },
                { headers: { 'X-CSRFToken': getCSRFToken() } }
            );

            console.log('CTE group added:', response.data);
            setNewGroupName('');
            // Refresh data
            await fetchGradeLevelData(selectedGradeLevel);
        } catch (error) {
            const detail = error?.response?.data
                ? JSON.stringify(error.response.data)
                : error.message;
            console.error('Error adding CTE group:', detail);
            alert('Error adding group. Please try again.');
        }
    };

    return (
        <DndProvider backend={HTML5Backend}>
            <div className="groups-home">
                <h2>Manage CTE Groups</h2>
                <div className="top-section">
                    <div className="filter-group">
                        <label htmlFor="grade-level-select">Grade Level:</label>
                        <select
                            id="grade-level-select"
                            value={selectedGradeLevel}
                            onChange={(e) => {
                                const val = e.target.value;
                                setSelectedGradeLevel(val);
                                if (val) {
                                    lastValidGradeLevelRef.current = val;
                                }
                            }}
                            disabled={isLoading}
                        >
                            <option value="">Select Grade Level</option>
                            {gradeLevels.map(grade => (
                                <option key={grade.id} value={grade.id}>{grade.grade}</option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-group">
                        <input
                            type="text"
                            value={newGroupName}
                            onChange={e => setNewGroupName(e.target.value)}
                            placeholder="Enter new group name"
                            disabled={isLoading}
                        />
                        <button 
                            onClick={addGroupManually} 
                            disabled={isLoading || !selectedGradeLevel}
                        >
                            Add Group
                        </button>
                    </div>
                </div>

                {isLoading && <div className="loading">Loading...</div>}

                <div className="layout">
                    <UnassignedStudentsCTE
                        students={groupData.cte?.unassignedStudents || []}
                        onDrop={item => handleDrop(item, null)}
                    />
                    <div className="group-list">
                        {groupData.cte?.groups.map(group => (
                            <div key={group.id} className="group-container">
                                <div className="group-header">
                                    <h3>{group.name}</h3>
                                    <RemoveGroup
                                        groupId={group.id}
                                        groupName={group.name}
                                        groupType="cte"
                                        onRemove={(removedGroupId, removedStudents) =>
                                            setGroupData(prevGroupData => {
                                                const updatedGroups = prevGroupData.cte.groups.filter(
                                                    group => group.id !== removedGroupId
                                                );
                                                const updatedUnassignedStudents = [
                                                    ...prevGroupData.cte.unassignedStudents,
                                                    ...removedStudents.map(student => ({
                                                        id: student.id,
                                                        first_name: student.first_name,
                                                        last_name: student.last_name,
                                                    })),
                                                ];
                                                return {
                                                    ...prevGroupData,
                                                    cte: {
                                                        groups: updatedGroups,
                                                        unassignedStudents: updatedUnassignedStudents,
                                                    },
                                                };
                                            })
                                        }
                                    />
                                </div>
                                <DroppableGroupCTE group={group} onDrop={handleDrop} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </DndProvider>
    );
};

export default ManageCTEGroups;
